/**
 * Modal para mostrar el detalle de errores de archivos de participantes.
 *
 * Este componente proporciona una interfaz para visualizar información detallada
 * de errores específicos, incluyendo datos de la fila, formato esperado y
 * sugerencias para corrección.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import {
  Box,
  Divider,
  Icon,
  Modal,
  Typography,
  Card,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AccordionDetails,
  AccordionSummary,
  Accordion,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";

export function ParticipantFileReportsDetail({ open, handleClose, errorData }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  
  const { text } = colors;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "800px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    maxHeight: "90%",
    backgroundColor: darkMode ? "#202940" : "#ffffff",
  };

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "Campo vacío";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Fecha inválida"
      : date.toISOString().slice(0, 19).replace("T", " ");
  };

  /**
   * Obtiene el color del chip según la severidad
   * @param {string} severity - Severidad del error
   * @returns {string} Color del chip
   */
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
      case "crítica":
        return "error";
      case "high":
      case "alta":
        return "warning";
      case "medium":
      case "media":
        return "info";
      case "low":
      case "baja":
        return "success";
      default:
        return "default";
    }
  };

  /**
   * Obtiene el ícono según la severidad
   * @param {string} severity - Severidad del error
   * @returns {JSX.Element} Ícono correspondiente
   */
  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
      case "crítica":
        return <ErrorIcon fontSize="small" />;
      case "high":
      case "alta":
        return <WarningIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  /**
   * Renderiza los datos de la fila en formato tabla
   * @param {Object|Array} rowData - Datos de la fila
   * @returns {JSX.Element} Tabla con los datos
   */
  const renderRowData = (rowData) => {
    if (!rowData) return <Typography color={darkMode ? "#ffffff" : "inherit"}>No hay datos de la fila disponibles</Typography>;

    let dataToRender = rowData;

    // Si es el formato formateado del serializer
    if (errorData.row_data_formatted) {
      dataToRender = errorData.row_data_formatted;
    }

    // Si es un objeto
    if (typeof dataToRender === "object" && !Array.isArray(dataToRender)) {
      return (
        <TableContainer component={Paper} sx={{
          maxHeight: 300,
          backgroundColor: darkMode ? "#1a2035" : "#ffffff",
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>
                  <strong>Campo</strong>
                </TableCell>
                <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>
                  <strong>Valor</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(dataToRender).map(([key, value], index) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>{key}</TableCell>
                  <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>{String(value || "Campo vacío")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Si es un array
    if (Array.isArray(dataToRender)) {
      const fieldNames = [
        "Nombre",
        "Apellido",
        "Documento",
        "Género",
        "Correo electrónico",
        "Teléfono de contacto",
        "Teléfono de emergencia",
        "Ubicación",
        "Estado",
        "Estado Activo",
        "Vínculo Laboral",
        "Programa",
        "Semestre",
      ];

      return (
        <TableContainer component={Paper} sx={{
          maxHeight: 300,
          backgroundColor: darkMode ? "#1a2035" : "#ffffff",
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>
                  <strong>Campo</strong>
                </TableCell>
                <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>
                  <strong>Valor</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataToRender.map((value, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>{fieldNames[index] || `Campo ${index + 1}`}</TableCell>
                  <TableCell sx={{ color: darkMode ? "#ffffff" : "inherit", borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit" }}>{String(value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Si es string o otro tipo
    return (
      <Typography variant="body2" sx={{
        p: 2,
        bgcolor: darkMode ? "#1a2035" : "#f5f5f5",
        color: darkMode ? "#ffffff" : "inherit",
        borderRadius: 1
      }}>
        {String(dataToRender)}
      </Typography>
    );
  };

  if (!errorData) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography id="modal-modal-title" component="h2">
            Detalle del error - Fila {errorData.row_number}
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
            }}
            onClick={handleClose}>
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />

        <Box style={{ overflowY: "auto" }} sx={{ mt: 2 }}>
          {/* Información básica del error */}
          <Typography variant="h6" gutterBottom sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
            Información del error
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Fila:</strong> {errorData.row_number}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Columna:</strong> {errorData.column_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Tipo de error:</strong>{" "}
                {errorData.error_type_display || errorData.error_type}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Severidad:</strong>{" "}
                <Chip
                  icon={getSeverityIcon(errorData.severity)}
                  label={errorData.severity_display || errorData.severity}
                  color={getSeverityColor(errorData.severity)}
                  size="small"
                />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Campo:</strong> {errorData.field_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Valor:</strong> {errorData.field_value || "Campo vacío"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Mensaje:</strong> {errorData.error_message}
              </Typography>
            </Grid>
            {errorData.expected_format && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                  <strong>Formato esperado:</strong> {errorData.expected_format}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Fecha del error:</strong> {formatDate(errorData.created)}
              </Typography>
            </Grid>
          </Grid>

          {/* Información del archivo */}
          <Typography variant="h6" gutterBottom sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
            Información del archivo
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Archivo:</strong> {errorData.upload_filename}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                <strong>Estado del archivo:</strong> {errorData.upload_status}
              </Typography>
            </Grid>
          </Grid>

          {/* Datos de la fila */}
          {/* <Typography variant="h6" gutterBottom>
            Datos de la Fila Completa
          </Typography> */}
          <Accordion sx={{
            backgroundColor: darkMode ? "#1a2035" : "#ffffff",
            color: darkMode ? "#ffffff" : "inherit",
          }}>
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon sx={{ color: darkMode ? "#ffffff" : "inherit" }} />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{
                backgroundColor: darkMode ? "#1a2035" : "#ffffff",
                borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "inherit",
              }}>
              <Typography component="h2" variant="h6" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                Datos de la fila completa
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: darkMode ? "#1a2035" : "#ffffff" }}>
              <Box sx={{ mb: 3 }}>{renderRowData(errorData.row_data)}</Box>
            </AccordionDetails>
          </Accordion>

          {/* Sugerencias de corrección */}
          {errorData.is_critical && (
            <Box sx={{
              p: 2,
              bgcolor: darkMode ? "#4a1a1a" : "#ffebee",
              borderRadius: 1,
              mb: 2,
              border: darkMode ? "1px solid #d32f2f" : "none",
            }}>
              <Typography variant="h6" color="error" gutterBottom sx={{ color: darkMode ? "#ff5252" : "error" }}>
                <ErrorIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Error Crítico
              </Typography>
              <Typography variant="body2" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                Este error requiere atención inmediata ya que impide el procesamiento correcto de la
                fila.
              </Typography>
            </Box>
          )}
        </Box>

        <Divider variant="middle" />
        <MDBox
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}>
          <MDButton color="info" onClick={handleClose}>
            Cerrar
          </MDButton>
        </MDBox>
      </Card>
    </Modal>
  );
}

ParticipantFileReportsDetail.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  errorData: PropTypes.object,
};
